import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  SelectChangeEvent,
  Box,
  TableContainer,
  Paper,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import IndexHeader from "app/shared-components/IndexHeader";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { ProgramData } from "src/app/@types/interfaces/youth.dashboard";
import { dashboardService } from "src/app/services/api/dashboard.service";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#202441",
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
  "&:last-child td, &:last-child th": { border: 0 },
  "&:hover": { backgroundColor: theme.palette.action.selected },
}));

const ProgramSelector: React.FC<{
  value: string;
  onChange: (e: SelectChangeEvent<string>) => void;
  label: string;
  programs: ProgramData[];
}> = ({ value, onChange, label, programs }) => (
  <Box sx={{ mb: 2 }}>
    <FormControl size="small" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select value={value} label={label} onChange={onChange}>
        {programs.map((p) => (
          <MenuItem key={p.name} value={p.name}>
            {p.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <Typography
    variant="body2"
    color="text.secondary"
    align="center"
    sx={{ mt: 4 }}
  >
    {message}
  </Typography>
);

const LoadingState: React.FC = () => (
  <>
    <IndexHeader
      name="Youth Dashboard"
      update={false}
      add={false}
      redirectingPath="/youth-dashboard"
    />
    <Box
      className="m-24"
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="400px"
    >
      <CircularProgress size={60} />
    </Box>
  </>
);

const ErrorState: React.FC<{ error: string }> = ({ error }) => (
  <>
    <IndexHeader
      name="Youth Dashboard"
      update={false}
      add={false}
      redirectingPath="/youth-dashboard"
    />
    <Box className="m-24">
      <Typography variant="h6" color="error" align="center">
        {error}
      </Typography>
      <Box display="flex" justifyContent="center" mt={2}>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    </Box>
  </>
);

const YouthDashboard: React.FC = () => {
  const [programs, setPrograms] = useState<ProgramData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selections, setSelections] = useState({
    applicationStatus: "",
    jobOpenings: "",
    importantDates: "",
    documents: "",
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await dashboardService.getYouthDashboardData();

        if (response.success && response.data?.programs) {
          setPrograms(response.data.programs);
          const firstProgram = response.data.programs[0]?.name || "";
          setSelections({
            applicationStatus: firstProgram,
            jobOpenings: firstProgram,
            importantDates: firstProgram,
            documents: firstProgram,
          });
        } else {
          setError(response.message || "Failed to load dashboard data");
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load dashboard data"
        );
        console.error("Error loading dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSelectionChange = useCallback(
    (field: keyof typeof selections) => (e: SelectChangeEvent<string>) => {
      setSelections((prev) => ({ ...prev, [field]: e.target.value }));
    },
    []
  );

  const getData = useCallback(
    (programName: string) =>
      programs.find((p) => p.name === programName) || programs[0],
    [programs]
  );

  const chartConfigs = useMemo(() => {
    const appData = getData(selections.applicationStatus);
    const jobData = getData(selections.jobOpenings);

    return {
      pie: {
        series: appData?.applicationStatus?.map((s) => s.count) || [],
        options: {
          labels: appData?.applicationStatus?.map((s) => s.status) || [],
          legend: { position: "bottom" as const },
          chart: { type: "pie" as const, id: "application-status-chart" },
          plotOptions: { pie: { dataLabels: { offset: -5 } } },
          dataLabels: {
            enabled: true,
            formatter: (val: number, opts: any) =>
              opts.w.config.series[opts.seriesIndex],
            style: { fontSize: "12px", fontWeight: "bold" },
          },
          tooltip: { y: { formatter: (val: number) => `${val} applications` } },
          responsive: [
            {
              breakpoint: 480,
              options: {
                chart: { width: 200 },
                legend: { position: "bottom" as const },
              },
            },
          ],
        } as ApexOptions,
      },
      bar: {
        series: [
          {
            name: "Job Openings",
            data: jobData?.categoryJobOpenings?.map((c) => c.count) || [],
          },
        ],
        options: {
          chart: { type: "bar" as const, id: "job-openings-chart" },
          xaxis: {
            categories:
              jobData?.categoryJobOpenings?.map((c) => c.category) || [],
            labels: { rotate: -45 },
          },
          plotOptions: { bar: { horizontal: false } },
          yaxis: { title: { text: "Number of Openings" } },
        } as ApexOptions,
      },
    };
  }, [getData, selections.applicationStatus, selections.jobOpenings]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;
  if (!programs.length)
    return (
      <>
        <IndexHeader
          name="Youth Dashboard"
          update={false}
          add={false}
          redirectingPath="/youth-dashboard"
        />
        <Typography variant="h6" className="m-24" align="center">
          No programs available
        </Typography>
      </>
    );

  const renderChart = (
    type: "pie" | "bar",
    title: string,
    selection: string,
    field: keyof typeof selections,
    label: string
  ) => (
    <Grid item xs={12} md={6}>
      <Card variant="outlined" sx={{ height: "100%" }}>
        <CardContent>
          <ProgramSelector
            value={selection}
            onChange={handleSelectionChange(field)}
            label={label}
            programs={programs}
          />
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          {chartConfigs[type].series.length > 0 ||
          (type === "bar" && chartConfigs[type].series[0]?.data.length > 0) ? (
            <Chart
              key={`${type}-${selection}`}
              options={chartConfigs[type].options}
              series={chartConfigs[type].series}
              type={type}
              height={300}
            />
          ) : (
            <EmptyState
              message={`No ${title.toLowerCase()} data available for this program`}
            />
          )}
        </CardContent>
      </Card>
    </Grid>
  );

  const renderTable = (
    title: string,
    selection: string,
    field: keyof typeof selections,
    label: string,
    dataField: "importantDates" | "documents"
  ) => {
    const data = getData(selection);
    const items = data?.[dataField] || [];

    return (
      <Grid item xs={12} md={6}>
        <Card variant="outlined" sx={{ height: "100%" }}>
          <CardContent>
            <ProgramSelector
              value={selection}
              onChange={handleSelectionChange(field)}
              label={label}
              programs={programs}
            />
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>
                      {dataField === "importantDates"
                        ? "Event"
                        : "Document Name"}
                    </StyledTableCell>
                    <StyledTableCell>
                      {dataField === "importantDates" ? "Date" : "Action"}
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length > 0 ? (
                    items.map((item: any) => (
                      <StyledTableRow key={item.label}>
                        <StyledTableCell>{item.label}</StyledTableCell>
                        <StyledTableCell>
                          {dataField === "importantDates" ? (
                            item.value
                          ) : (
                            <Button
                              variant="contained"
                              color="secondary"
                              size="small"
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              disabled={item.url === "#"}
                            >
                              {item.url === "#" ? "Coming Soon" : "View"}
                            </Button>
                          )}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                  ) : (
                    <StyledTableRow>
                      <StyledTableCell colSpan={2} align="center">
                        <EmptyState
                          message={`No ${title.toLowerCase()} available for this program`}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <>
      <IndexHeader
        name="Youth Dashboard"
        update={false}
        add={false}
        redirectingPath="/youth-dashboard"
      />
      <Box className="m-24">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h5" sx={{ mb: -1 }}>
              Analytics Overview
            </Typography>
          </Grid>

          {renderChart(
            "pie",
            "Application Status",
            selections.applicationStatus,
            "applicationStatus",
            "Select Program for Application Status"
          )}
          {renderChart(
            "bar",
            "Category-wise Job Openings",
            selections.jobOpenings,
            "jobOpenings",
            "Select Program for Job Openings"
          )}

          <Grid item xs={12} sx={{ mt: 0 }}>
            <Typography variant="h5" sx={{ mb: -1 }}>
              Program Information
            </Typography>
          </Grid>

          {renderTable(
            "Important Dates",
            selections.importantDates,
            "importantDates",
            "Select Program for Important Dates",
            "importantDates"
          )}
          {renderTable(
            "Important Documents",
            selections.documents,
            "documents",
            "Select Program for Documents",
            "documents"
          )}
        </Grid>
      </Box>
    </>
  );
};

export default YouthDashboard;
